import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, X } from 'lucide-react';

const ReviewCard = ({ review }) => {
  const { author_name, rating, text, time } = review;
  const date = new Date(time).toLocaleDateString();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  // Handle escape key press
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) setIsModalOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow mx-2 flex flex-col h-full">
        {/* Rating Stars */}
        <div className="flex justify-center text-yellow-400 mb-4">
          {[...Array(Math.round(rating))].map((_, i) => (
            <Star key={i} className="h-5 w-5 fill-current" />
          ))}
        </div>

        {/* Review Text */}
        <div className="flex-grow flex flex-col">
          <p className="text-gray-600 mb-4 line-clamp-4">
            {text}
          </p>
          
          <button
            onClick={() => setIsModalOpen(true)}
            className="text-teal-600 hover:text-teal-700 text-sm font-medium mt-auto self-start"
          >
            Read More
          </button>
        </div>

        {/* Author and Date */}
        <h6 className="font-semibold mt-4 pt-2 border-t border-gray-100">
          {author_name} | {date}
        </h6>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setIsModalOpen(false)}
          />
          
          {/* Modal Content */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg max-w-2xl w-full p-6 shadow-xl">
              {/* Close Button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>

              {/* Modal Header */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold flex items-center space-x-2">
                  <span>{author_name}</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-600 text-sm">{date}</span>
                </h3>
                <div className="flex text-yellow-400 mt-2">
                  {[...Array(Math.round(rating))].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-current" />
                  ))}
                </div>
              </div>

              {/* Modal Body */}
              <div className="mt-4">
                <p className="text-gray-600 whitespace-pre-wrap">{text}</p>
              </div>

              {/* Modal Footer */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ReviewsCarousel = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const GOOGLE_PLACE_ID = process.env.REACT_APP_GOOGLE_PLACE_ID;
  const GOOGLE_REVIEW_BASE_URL = "https://search.google.com/local/reviews";

  const getReviewUrl = () => {
    if (GOOGLE_PLACE_ID) {
      return `${GOOGLE_REVIEW_BASE_URL}?placeid=${GOOGLE_PLACE_ID}`;
    }
    return "https://www.google.com/search?q=Nirvana+Career+Counselling+bangalore";
  };

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        console.log('Attempting to fetch reviews...');
        
        // Try to fetch from Netlify function first
        try {
          const response = await fetch('/.netlify/functions/google-reviews');
          if (response.ok) {
            const data = await response.json();
            if (data && Array.isArray(data.reviews) && data.reviews.length > 0) {
              setReviews(data.reviews);
              setLoading(false);
              return;
            }
          }
        } catch (fetchError) {
          console.log('Netlify function not available, using mock data');
        }

        // Fallback to mock data
        const mockReviews = [
          {
            author_name: "Rajesh Kumar Patel",
            rating: 5,
            text: "Exceptional career counselling service! Mr. Subhransu's guidance was instrumental in helping me transition from engineering to management. The psychometric assessment was incredibly accurate and helped identify my core strengths. The personalized career roadmap he provided has been my guiding light. Highly recommend for anyone serious about their career growth.",
            time: Date.now() - 86400000 // 1 day ago
          },
          {
            author_name: "Sneha Agarwal",
            rating: 5,
            text: "Perfect guidance for my daughter's career planning after 12th grade. The session was comprehensive and helped us understand various career options we hadn't even considered. Mr. Subhransu explained everything in detail and made the complex world of career choices simple to understand. The follow-up support has been excellent too.",
            time: Date.now() - 172800000 // 2 days ago
          },
          {
            author_name: "Dr. Vikram Singh",
            rating: 5,
            text: "As a working professional looking to make a strategic career move, I found the counselling session incredibly valuable. The insights provided about industry trends and skill requirements were spot-on. The session helped me chart a clear path for my career advancement. Professional, insightful, and highly recommended!",
            time: Date.now() - 259200000 // 3 days ago
          },
          {
            author_name: "Anita Sharma",
            rating: 5,
            text: "Wonderful experience! The career counselling helped my son choose the right engineering specialization. The detailed analysis of his interests and aptitudes was very thorough. We now have a clear 4-year plan for his education and career. Thank you for the excellent guidance!",
            time: Date.now() - 345600000 // 4 days ago
          },
          {
            author_name: "Rohit Mishra",
            rating: 5,
            text: "Fantastic career counselling service. I was confused about whether to pursue MBA or switch to a technical role. The counselling session provided clarity and helped me make an informed decision. The online session was smooth and professional. Great value for money!",
            time: Date.now() - 432000000 // 5 days ago
          },
          {
            author_name: "Meera Joshi",
            rating: 5,
            text: "Highly professional and knowledgeable counsellor. The career guidance session was detailed and personalized. Mr. Subhransu understood my background and provided practical advice for my career transition. The session exceeded my expectations in every way.",
            time: Date.now() - 518400000 // 6 days ago
          }
        ];

        // Simulate network delay
        setTimeout(() => {
          setReviews(mockReviews);
          setLoading(false);
        }, 500);

      } catch (err) {
        console.error('Error in fetchReviews:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Get reviews for current page
  const getCurrentPageReviews = () => {
    const isDesktop = window.innerWidth >= 1024;
    const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
    const baseItemsPerPage = isDesktop ? 3 : isTablet ? 2 : 1;
    
    let startIndex = 0;
    let endIndex = 0;
    
    if (currentPage === 0) {
      startIndex = 0;
      endIndex = baseItemsPerPage;
    } else if (currentPage === 1 && isDesktop) {
      // Show 2 reviews on second page only for desktop
      startIndex = baseItemsPerPage;
      endIndex = startIndex + 2;
    } else {
      // For mobile and tablet, or any other page on desktop
      startIndex = currentPage === 1 ? baseItemsPerPage : baseItemsPerPage + 2 + (currentPage - 2) * baseItemsPerPage;
      endIndex = startIndex + baseItemsPerPage;
    }
    
    return reviews.slice(startIndex, endIndex);
  };

  // Calculate total pages
  const getTotalPages = () => {
    if (!reviews.length) return 1;
    
    const isDesktop = window.innerWidth >= 1024;
    const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
    const baseItemsPerPage = isDesktop ? 3 : isTablet ? 2 : 1;
    
    if (isDesktop) {
      // For desktop: First page (3) + Second page (2) + remaining pages (3 each)
      const remainingReviews = reviews.length - baseItemsPerPage - 2;
      return Math.ceil(2 + (remainingReviews > 0 ? remainingReviews / baseItemsPerPage : 0));
    } else {
      // For mobile/tablet: All pages show baseItemsPerPage reviews
      return Math.ceil(reviews.length / baseItemsPerPage);
    }
  };

  const nextSlide = () => {
    setCurrentPage((prev) => (prev + 1) % getTotalPages());
  };

  const prevSlide = () => {
    setCurrentPage((prev) => (prev - 1 + getTotalPages()) % getTotalPages());
  };

  if (loading) {
    return (
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <div className="w-24 h-1 bg-teal-600 mx-auto"></div>
          </div>
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-600"></div>
            <span className="ml-3 text-gray-600">Loading reviews...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <div className="w-24 h-1 bg-teal-600 mx-auto"></div>
          </div>
          <div className="text-center text-gray-600 py-8">
            <p>Reviews are temporarily unavailable.</p>
            <p className="text-sm mt-2">Please check back later or contact us directly.</p>
          </div>
        </div>
      </div>
    );
  }

  const currentReviews = getCurrentPageReviews();
  const totalPages = getTotalPages();
  const showNavigation = reviews.length > 3;

  return (
    <div className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
          <div className="w-24 h-1 bg-teal-600 mx-auto"></div>
        </div>
        
        <div className="relative">
          {showNavigation && (
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -ml-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 z-10"
              aria-label="Previous reviews"
            >
              <ChevronLeft className="h-6 w-6 text-teal-600" />
            </button>
          )}

          <div className="overflow-hidden">
            <div className={`grid gap-8 ${
              currentPage === 1 && window.innerWidth >= 1024
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2' 
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {currentReviews.map((review, index) => (
                <ReviewCard key={`${review.time}-${index}`} review={review} />
              ))}
            </div>
          </div>

          {showNavigation && (
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 -mr-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 z-10"
              aria-label="Next reviews"
            >
              <ChevronRight className="h-6 w-6 text-teal-600" />
            </button>
          )}
        </div>

        <div className="flex justify-center space-x-2 mt-6">
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPage(index)}
              className={`h-2 w-2 rounded-full transition-all ${
                currentPage === index ? 'bg-teal-600 w-4' : 'bg-gray-300'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <div className="flex justify-center mt-8">
          <a 
            href={getReviewUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-6 py-3 border-2 border-teal-600 text-teal-600 rounded-md hover:bg-teal-50 transition-colors"
          >
            View All Reviews
          </a>
        </div>
      </div>
    </div>
  );
};

export default ReviewsCarousel;